import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useWorkspace } from "@/hooks/useWorkspace";
import { CONTENT_TYPE_OPTIONS, createUserTemplate } from "@/lib/composerTemplates";
import type { Json } from "@/integrations/supabase/types";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill from an existing brief: title, content type, wizard answers, structure */
  defaults?: {
    name?: string;
    contentType?: string;
    description?: string;
    introMessage?: string;
    wizardQuestions?: Json;
    defaultStructure?: Json;
  };
  onCreated?: () => void;
};

export const SaveAsTemplateDialog = ({ open, onOpenChange, defaults, onCreated }: Props) => {
  const { active } = useWorkspace();
  const [name, setName] = useState("");
  const [contentType, setContentType] = useState("other");
  const [description, setDescription] = useState("");
  const [introMessage, setIntroMessage] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(defaults?.name ?? "");
    setContentType(defaults?.contentType ?? "other");
    setDescription(defaults?.description ?? "");
    setIntroMessage(defaults?.introMessage ?? "");
  }, [open, defaults]);

  const handleSave = async () => {
    if (!active) return;
    if (!name.trim()) {
      toast.error("Give the template a name");
      return;
    }
    setSaving(true);
    try {
      await createUserTemplate(active.id, {
        name: name.trim(),
        content_type: contentType,
        description: description.trim() || null,
        intro_message: introMessage.trim() || null,
        wizard_questions: defaults?.wizardQuestions ?? [],
        default_structure: defaults?.defaultStructure ?? [],
      });
      toast.success("Template saved");
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save as template</DialogTitle>
          <DialogDescription>
            Save this piece's setup as a reusable template for this workspace.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="tpl-name">Template name</Label>
            <Input id="tpl-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="tpl-type">Content type</Label>
            <Select value={contentType} onValueChange={setContentType}>
              <SelectTrigger id="tpl-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTENT_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="tpl-desc">Description (optional)</Label>
            <Textarea
              id="tpl-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="tpl-intro">Intro message (optional)</Label>
            <Textarea
              id="tpl-intro"
              value={introMessage}
              onChange={(e) => setIntroMessage(e.target.value)}
              rows={3}
              placeholder="Shown at the top of the chat when this template is used."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Save template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
